import React from "react";
import { motion } from "framer-motion";
import {
  FaRegLightbulb,
  FaShieldAlt,
  FaGlobeAsia,
  FaUsers,
  FaRegHandshake,
  FaChartLine,
  FaPenNib,
  FaQuoteLeft,
  FaCheckCircle,
  FaTrophy,
  FaMedal,
  FaAward
} from "react-icons/fa";
import { BsArrowRight } from "react-icons/bs";

const About = () => {
  return (
    <div className="bg-white text-gray-800 overflow-hidden font-sans">
      
      {/* ================= HERO BANNER ================= */}
      <div className="relative w-full bg-brand-purple py-20 lg:py-28 overflow-hidden flex items-center justify-center min-h-[50vh]">
        {/* Simple Background Pattern */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" />
        
        {/* Content */}
        <div className="max-w-[1400px] mx-auto px-4 relative z-10 text-center flex flex-col items-center">
          <motion.span 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block py-1 px-4 rounded-full bg-white text-brand-purple text-xs md:text-sm font-bold tracking-[0.2em] uppercase mb-6 shadow-sm"
          >
            आवाज़ों को सशक्त बनाना, सत्य को पहुँचाना
          </motion.span>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-5xl md:text-6xl lg:text-7xl font-black text-white capitalize tracking-tight mb-8 drop-shadow-md leading-tight"
          >
             द मंदाकिनी टाइम्स
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="max-w-3xl mx-auto text-gray-200 text-lg md:text-2xl font-medium mb-4 leading-relaxed"
          >
            सच्चाई की धड़कन। हम केवल खबरें ही नहीं देते, हम उन कहानियों को प्रकाश में लाते हैं जो समाज का मार्गदर्शन करती हैं और बदलाव लाती हैं।
          </motion.p>
        </div>
      </div>

      {/* ================= OUR STORY ================= */}
      <section className="py-16 md:py-24 bg-white relative">
        <div className="max-w-[1400px] mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start">
            
            {/* Story Text (Increased Width) */}
            <motion.div 
              className="lg:w-7/12"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="flex items-center gap-4 mb-6">
                <span className="w-12 h-1 bg-brand-red rounded-full" />
                <h2 className="text-sm font-bold tracking-widest text-brand-purple uppercase">हमारी कहानी</h2>
              </div>
              
              <h3 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6 leading-snug tracking-tight">
                ज़मीनी स्तर की पत्रकारिता, <br />
                <span className="text-brand-purple">बिना किसी समझौते के।</span>
              </h3>
              
              <div className="space-y-5 text-lg text-gray-600 relative pl-6 border-l-4 border-brand-red/30">
                <p className="leading-relaxed">
                  <strong className="text-gray-900 font-bold">द मंदाकिनी टाइम्स</strong> केवल एक साधारण न्यूज़ पोर्टल नहीं है, बल्कि एक <span className="text-brand-purple font-bold">मिशन</span> है। इसकी नींव एक बहुत स्पष्ट और शक्तिशाली विचार पर रखी गई थी— <strong className="text-gray-900 font-bold bg-gray-100 px-2 py-0.5 rounded whitespace-nowrap">"सत्य बिना किसी डर के हर नागरिक तक पहुंचना चाहिए।"</strong>
                </p>
                <p className="leading-relaxed">
                  जबकि मुख्यधारा की मीडिया महानगरों की चकाचौंध में खोई हुई है, हमने अनसुनी आवाज़ों को अपना मंच बनाया, जो समाज के हाशिये पर छूट गए थे। हम ऐसी कहानियाँ लाते हैं जो सिर्फ खबर नहीं, बल्कि आपके जीवन का हिस्सा हैं।
                </p>
                <p className="leading-relaxed">
                  शुरुआती दिनों में, हमारे पास संसाधन कम थे लेकिन असीम साहस था। हम एक गाँव से दूसरे गाँव गए और उन मुद्दों को उठाया जिन पर ध्यान नहीं दिया गया था। चाहे किसानों की समस्याएँ हों, भ्रष्टाचार के मामले हों, या युवाओं का रोज़गार, हमने हर आवाज़ को बुलंद किया।
                </p>
                <p className="leading-relaxed">
                  आज, <strong className="text-gray-900 font-bold">द मंदाकिनी टाइम्स</strong> केवल एक नाम नहीं, बल्कि विश्वास का प्रतीक है। हमारी टीम दिन-रात काम करती है ताकि यह सुनिश्चित हो सके कि हर महत्वपूर्ण खबर आप तक पहुंचे। हम किसी राजनीतिक दबाव या कॉर्पोरेट एजेंडे के आगे नहीं झुकते।
                </p>
                <p className="leading-relaxed">
                  फर्जी खबरों और सनसनीखेज से भरे डिजिटल युग में, हमारा संकल्प पूरी जिम्मेदारी और प्रामाणिकता के साथ तथ्य-आधारित पत्रकारिता को जारी रखना है। क्योंकि लोकतंत्र तभी मजबूत होता है जब इसके नागरिक सही जानकारी से लैस हों।
                </p>

                <div className="pt-6 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-brand-purple/10 text-brand-purple">
                      <FaCheckCircle className="text-2xl" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">100% स्वतंत्र</h4>
                      <p className="text-sm text-gray-500">राजनीतिक दबाव से मुक्त</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-brand-red/10 text-brand-red">
                      <FaShieldAlt className="text-2xl" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">तथ्य आधारित</h4>
                      <p className="text-sm text-gray-500">पूरी तरह से सत्यापित खबरें</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Premium Graphic (Decreased Width) */}
            <motion.div 
              className="lg:w-5/12 w-full relative lg:sticky lg:top-32"
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              <div className="relative w-full max-w-md mx-auto aspect-[3/4]">
                <div className="absolute inset-0 bg-brand-red rounded-3xl rotate-3 scale-105 opacity-10" />
                
                <div className="relative w-full h-full bg-brand-purple rounded-3xl shadow-xl overflow-hidden flex flex-col group">
                  <div className="h-3/5 w-full overflow-hidden">
                    <img src="https://images.unsplash.com/photo-1585829365295-ab7cd400c167?q=80&w=800&auto=format&fit=crop" alt="Independent Media" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="h-2/5 w-full bg-brand-purple p-8 text-white flex flex-col justify-center">
                    <h4 className="text-3xl font-black mb-2">स्वतंत्र मीडिया</h4>
                    <p className="text-purple-100 text-sm leading-relaxed">हम किसी भी कॉर्पोरेट दबाव से पूरी तरह मुक्त हैं। हमारी निष्ठा पूरी तरह से हमारे पाठकों के प्रति है।</p>
                  </div>
                </div>
                
                {/* Floating badge */}
                <motion.div 
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -right-4 top-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl p-4 border border-gray-100 flex items-center gap-3 z-20"
                >
                  <div className="w-12 h-12 bg-brand-red rounded-full flex items-center justify-center text-white font-bold text-xl">
                    <FaUsers />
                  </div>
                  <div>
                    <div className="text-xl font-black text-gray-900">1M+</div>
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">पाठक</div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ================= IMPACT STATISTICS ================= */}
      <section className="py-20 relative bg-brand-purple overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1504711434969-e33886168f5c?q=80&w=1400&auto=format&fit=crop" 
            alt="Impact Background" 
            className="w-full h-full object-cover opacity-30" 
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-purple via-brand-purple/95 to-brand-purple/90" />
        </div>
        
        <div className="max-w-[1400px] mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "1M+", label: "मासिक पाठक" },
              { number: "50+", label: "ग्राउंड रिपोर्टर्स" },
              { number: "100%", label: "स्वतंत्र और निष्पक्ष" },
              { number: "24/7", label: "सटीक समाचार अपडेट" }
            ].map((stat, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center md:border-r border-white/20 last:border-0"
              >
                <div className="text-4xl md:text-5xl font-black text-white mb-2 drop-shadow-md">
                  {stat.number}
                </div>
                <div className="text-purple-100 font-bold text-sm uppercase tracking-wide">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= AWARDS & RECOGNITION ================= */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-sm font-bold tracking-widest text-brand-red uppercase mb-2">हमारी उपलब्धियां</h2>
            <h3 className="text-3xl md:text-4xl font-black text-gray-900">पुरस्कार और सम्मान</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { img: "/images/awards/award_digital_news_1786448248379.jpg", title: "सर्वश्रेष्ठ डिजिटल समाचार", year: "2024", desc: "डिजिटल मीडिया में उत्कृष्टता के लिए राष्ट्रीय पुरस्कार।" },
              { img: "/images/awards/award_ground_reporting_1786448324059.jpg", title: "ग्राउंड रिपोर्टिंग", year: "2023", desc: "ग्रामीण भारत के महत्वपूर्ण मुद्दों को उठाने के लिए।" },
              { img: "/images/awards/award_trusted_news_1786448361637.jpg", title: "विश्वसनीय स्थानीय समाचार", year: "2022", desc: "पाठकों के बीच सबसे भरोसेमंद समाचार मंच होने का सम्मान।" },
              { img: "/images/awards/award_excellence_journalism_1786448403659.jpg", title: "पत्रकारिता में उत्कृष्टता", year: "2021", desc: "निष्पक्ष पत्रकारिता के लिए विशेष मान्यता।" }
            ].map((award, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-3xl overflow-hidden border border-gray-100 flex flex-col items-center text-center hover:shadow-xl hover:border-brand-red transition-all duration-300 group"
              >
                <div className="w-full aspect-[4/3] overflow-hidden bg-white rounded-t-3xl border-b border-gray-100 flex items-center justify-center">
                  <img 
                    src={award.img} 
                    alt={award.title} 
                    className="w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex flex-col items-center w-full">
                  <div className="text-sm font-bold text-gray-400 mb-2 tracking-widest">{award.year}</div>
                  <h4 className="text-xl font-bold text-brand-purple mb-3">{award.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{award.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= EDITOR'S MESSAGE ================= */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-brand-red text-white rounded-3xl overflow-hidden shadow-xl flex flex-col md:flex-row relative"
          >
            {/* Image Side */}
            <div className="md:w-5/12 relative bg-gray-900">
              <img 
                src="https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=800&auto=format&fit=crop" 
                alt="Founder & Editor" 
                className="w-full h-full object-cover object-top min-h-[350px] opacity-90"
              />
            </div>

            {/* Content Side */}
            <div className="md:w-7/12 p-8 md:p-12 lg:p-16 flex flex-col justify-center">
              <FaQuoteLeft className="text-5xl text-white/30 mb-6" />
              <h3 className="text-xl md:text-2xl font-medium leading-relaxed mb-8 italic text-red-50">
                "हमारा दृष्टिकोण केवल खबरें देना नहीं है, बल्कि उस सच्चाई को सामने लाना है जो अक्सर फाइलों और बयानों के बीच छिप जाती है। लोकतंत्र में, सटीक जानकारी हर नागरिक का अधिकार है।"
              </h3>
              
              <div>
                <h4 className="text-white text-2xl font-bold">संपादक का नाम</h4>
                <p className="text-red-200 font-bold uppercase tracking-wider text-sm mt-1">संस्थापक और प्रधान संपादक</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= WHAT WE COVER ================= */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-sm font-bold tracking-widest text-brand-red uppercase mb-2">हमारी कवरेज</h2>
            <h3 className="text-3xl md:text-4xl font-black text-gray-900">हम क्या कवर करते हैं</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "राष्ट्रीय", img: "https://images.unsplash.com/photo-1541872703-74c5e44368f9?q=80&w=600&auto=format&fit=crop" },
              { title: "विश्व", img: "https://images.unsplash.com/photo-1521295121783-8a321d551ad2?q=80&w=600&auto=format&fit=crop" },
              { title: "राजनीति", img: "https://images.unsplash.com/photo-1540910419892-4a36d2c3266c?q=80&w=600&auto=format&fit=crop" },
              { title: "व्यापार", img: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?q=80&w=600&auto=format&fit=crop" },
              { title: "मनोरंजन", img: "https://images.unsplash.com/photo-1598899134739-24c46f58b8c0?q=80&w=600&auto=format&fit=crop" },
              { title: "खेल", img: "https://images.unsplash.com/photo-1517649763962-0c623066013b?q=80&w=600&auto=format&fit=crop" },
              { title: "तकनीक/विज्ञान", img: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop" },
              { title: "धर्म/ज्योतिष", img: "https://images.unsplash.com/photo-1604085572504-a392ddf0d86a?q=80&w=600&auto=format&fit=crop" }
            ].map((topic, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                viewport={{ once: true }}
                className="group relative rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white aspect-[4/3] cursor-pointer"
              >
                <img 
                  src={topic.img} 
                  alt={topic.title} 
                  className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-brand-purple/60 transition-colors duration-300" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                  <h4 className="text-2xl font-bold drop-shadow-md">{topic.title}</h4>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CORE VALUES ================= */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-sm font-bold tracking-widest text-brand-purple uppercase mb-2">हमारे मूल मूल्य</h2>
            <h3 className="text-3xl md:text-4xl font-black text-gray-900">हम किन सिद्धांतों का पालन करते हैं?</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: <FaShieldAlt />, title: "सत्यनिष्ठा", subtitle: "सत्यनिष्ठा", desc: "हम जो भी प्रकाशित करते हैं उसकी पूरी तरह से जाँच और पुष्टि की जाती है। यहाँ अफवाहों के लिए कोई जगह नहीं है।" },
              { icon: <FaGlobeAsia />, title: "हाइपरलोकल", subtitle: "हाइपरलोकल", desc: "हम सिर्फ बड़े शहरों की ही नहीं, बल्कि हाशिये पर पड़े हर वर्ग की आवाज़ हैं।" },
              { icon: <FaRegHandshake />, title: "निष्पक्षता", subtitle: "निष्पक्षता", desc: "हमारा मंच सभी पक्षों को अपने विचार व्यक्त करने का समान अवसर प्रदान करता है। हम किसी एक विचारधारा से बंधे नहीं हैं।" },
              { icon: <FaRegLightbulb />, title: "प्रभाव", subtitle: "प्रभाव", desc: "हमारा उद्देश्य केवल खामियां खोजना नहीं है, बल्कि समाधानों पर चर्चा करना और समाज में सकारात्मक बदलाव लाना है।" }
            ].map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:border-brand-purple transition-colors duration-300"
              >
                <div className="w-14 h-14 rounded-xl bg-white text-brand-purple text-2xl flex items-center justify-center mb-6 shadow-sm">
                  {value.icon}
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-1">{value.title}</h4>
                <p className="text-xs font-bold text-brand-red uppercase tracking-wider mb-4">{value.subtitle}</p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {value.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="py-20 bg-brand-purple text-white text-center">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-black mb-6 leading-tight">
              हमारा लक्ष्य सिर्फ समाचार देना नहीं, बल्कि समाज में जागरूकता लाना है।
            </h2>
            <p className="text-lg text-purple-100 mb-10 max-w-2xl mx-auto">
              द मंदाकिनी टाइम्स परिवार का हिस्सा बनें और सच्ची पत्रकारिता का समर्थन करें।
            </p>
            
            <a href="/contact" className="inline-flex items-center justify-center gap-3 bg-white text-brand-purple px-10 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors shadow-lg">
              संपर्क करें <BsArrowRight className="text-xl" />
            </a>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default About;
